import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, History, Loader2, Pencil, RefreshCw, Settings, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import ScopeMatrix from '../../components/developer-api/ScopeMatrix';
import RevealKeyDialog from '../../components/developer-api/RevealKeyDialog';
import { RevokeKeyDialog, RollKeyDialog } from '../../components/developer-api/KeyActionDialogs';
import { KeyStatusPill, Method, ScopeChips, StatTile, StatusCode } from '../../components/developer-api/parts';
import { errorText } from '../../lib/apiErrors';
import { EXPIRY_OPTIONS, formatDate, getKey, listRequests, listTeam, relativeTime, scopeSentence, updateKey } from '../../lib/developerApi';
import { formatLogTime } from '../../components/developer-api/parts';

/** Admin → Developer API → one key: usage, permissions (editable), recent requests, settings. */
export default function AdminDeveloperApiKeyPage() {
    const { keyId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getAuthHeader } = useAuth();
    const token = getAuthHeader()?.Authorization;
    const headers = useMemo(() => (token ? { Authorization: token } : {}), [token]);

    const [apiKey, setApiKey] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // scopes draft
    const [saving, setSaving] = useState(false);
    const [rolling, setRolling] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [reveal, setReveal] = useState(null);
    const [settingsDraft, setSettingsDraft] = useState(null); // { ownerId, expiry }
    const [team, setTeam] = useState([]);

    const load = useCallback(async () => {
        if (!keyId) return;
        try {
            const [k, log] = await Promise.all([
                getKey(headers, keyId),
                listRequests(headers, { key_id: keyId, limit: 6 }),
            ]);
            setApiKey(k);
            setRecent(log.rows);
            // `?edit=permissions` (the keys table's "Edit permissions") opens the matrix.
            if (searchParams?.get?.('edit') === 'permissions' && k.status !== 'revoked') {
                setEditing((current) => current || [...(k.scopes || [])]);
            }
        } catch (err) {
            toast.error(errorText(err, 'Could not load this key.'));
        } finally {
            setLoading(false);
        }
    }, [headers, keyId]); // eslint-disable-line react-hooks/exhaustive-deps -- searchParams is read once, on load

    useEffect(() => {
        load();
    }, [load]);

    const back = () => navigate('/admin/developer-api');

    if (loading) {
        return (
            <AdminLayout title="Developer API">
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            </AdminLayout>
        );
    }
    if (!apiKey) {
        return (
            <AdminLayout title="Developer API">
                <button type="button" onClick={back} className="inline-flex items-center gap-1.5 text-[13px] text-stone-500 hover:text-stone-900"><ArrowLeft className="w-4 h-4" /> Developer API</button>
                <p className="text-sm text-stone-500 mt-4">This key does not exist in your organization.</p>
            </AdminLayout>
        );
    }

    const revoked = apiKey.status === 'revoked';
    const errorRate = apiKey.requests_7d ? `${((apiKey.errors_7d / apiKey.requests_7d) * 100).toFixed(1)}%` : '—';

    const savePermissions = async () => {
        setSaving(true);
        try {
            const updated = await updateKey(headers, apiKey.key_id, { scopes: editing });
            setApiKey((current) => ({ ...current, ...updated }));
            setEditing(null);
            toast.success('Permissions saved. They apply to the next request.');
        } catch (err) {
            toast.error(errorText(err, 'Could not save permissions.'));
        } finally {
            setSaving(false);
        }
    };

    // Settings edit: the default owner and the expiry are what "Needs attention"
    // asks the admin to fix (rolling a key never moves its expiry).
    const editSettings = async () => {
        setSettingsDraft({ ownerId: apiKey.default_owner?.id || '', expiry: 'keep' });
        try {
            setTeam(await listTeam(headers));
        } catch (err) {
            toast.error(errorText(err, 'Could not load your team.'));
        }
    };

    const saveSettings = async () => {
        const body = {};
        if (settingsDraft.ownerId && settingsDraft.ownerId !== apiKey.default_owner?.id) body.default_owner_id = settingsDraft.ownerId;
        if (settingsDraft.expiry !== 'keep') {
            const days = EXPIRY_OPTIONS.find((o) => o.label === settingsDraft.expiry)?.days;
            if (days) body.expires_at = new Date(Date.now() + days * 86400000).toISOString();
            else body.clear_expiry = true;
        }
        if (!Object.keys(body).length) {
            setSettingsDraft(null);
            return;
        }
        setSaving(true);
        try {
            const updated = await updateKey(headers, apiKey.key_id, body);
            setApiKey((current) => ({ ...current, ...updated }));
            setSettingsDraft(null);
            toast.success('Settings saved.');
        } catch (err) {
            toast.error(errorText(err, 'Could not save settings.'));
        } finally {
            setSaving(false);
        }
    };

    const actions = revoked ? null : (
        <>
            {apiKey.status !== 'expired' && (
                <button type="button" onClick={() => setRolling(true)} className="h-9 px-3.5 rounded-md border border-stone-200 bg-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-50">
                    <RefreshCw className="w-4 h-4" /> Roll key
                </button>
            )}
            <button type="button" onClick={() => setRevoking(true)} className="h-9 px-3.5 rounded-md border border-stone-200 bg-white text-sm font-medium text-red-600 inline-flex items-center gap-2 hover:bg-red-50">
                <Trash2 className="w-4 h-4" /> Revoke
            </button>
        </>
    );

    return (
        <AdminLayout
            title={<span className="inline-flex items-center gap-2.5">{apiKey.name} <KeyStatusPill apiKey={apiKey} /></span>}
            description={`${apiKey.display} · created ${formatDate(apiKey.created_at)} by ${apiKey.created_by?.name || '—'} · last used ${relativeTime(apiKey.last_used_at)}`}
            actions={actions}
        >
            <button type="button" onClick={back} className="inline-flex items-center gap-1.5 text-[13px] text-stone-500 hover:text-stone-900 mb-4" data-testid="back-to-keys">
                <ArrowLeft className="w-4 h-4" /> Developer API
            </button>

            <div className="grid sm:grid-cols-3 gap-4">
                <StatTile label="Requests · 7d" value={(apiKey.requests_7d || 0).toLocaleString()} hint={`${(apiKey.requests_24h || 0).toLocaleString()} in the last 24 hours`} />
                <StatTile
                    label="Records written · 7d"
                    value={((apiKey.records_created_7d || 0) + (apiKey.records_updated_7d || 0)).toLocaleString()}
                    hint={`${apiKey.records_created_7d || 0} created · ${apiKey.records_updated_7d || 0} updated`}
                />
                <StatTile
                    label="Error rate · 7d"
                    value={errorRate}
                    hint={`${(apiKey.errors_7d || 0).toLocaleString()} failed · field errors, missing scopes, rate limits`}
                />
            </div>

            <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-4 mt-4 items-start">
                <div className="flex flex-col gap-4">
                    <section className="border border-stone-200 rounded-xl px-5 py-4" data-testid="permissions-card">
                        <div className="flex items-center gap-2 mb-3.5">
                            <Shield className="w-4 h-4 text-stone-500" />
                            <b className="flex-1 text-[15px] font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Permissions</b>
                            {!revoked && !editing && (
                                <button type="button" data-testid="edit-permissions" onClick={() => setEditing([...apiKey.scopes])} className="h-7 px-2.5 rounded-md border border-stone-200 text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                                    <Pencil className="w-3 h-3" /> Edit
                                </button>
                            )}
                        </div>
                        {editing ? (
                            <>
                                <ScopeMatrix value={editing} onChange={setEditing} />
                                <div className="text-[13px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5 mt-3">{scopeSentence(editing)}</div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <button type="button" onClick={() => setEditing(null)} className="h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium hover:bg-stone-50">Cancel</button>
                                    <button type="button" data-testid="save-permissions" disabled={!editing.length || saving} onClick={savePermissions} className="h-8 px-3 rounded-md bg-stone-900 text-white text-[13px] font-medium hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400">
                                        Save permissions
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <ScopeChips scopes={apiKey.scopes} />
                                <p className="text-[13px] text-stone-500 mt-2.5">
                                    {scopeSentence(apiKey.scopes)}{!revoked && ' Changes apply to the next request.'}
                                </p>
                            </>
                        )}
                    </section>

                    <section className="border border-stone-200 rounded-xl px-5 py-4">
                        <div className="flex items-center gap-2 mb-3.5">
                            <History className="w-4 h-4 text-stone-500" />
                            <b className="flex-1 text-[15px] font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent requests</b>
                            <button type="button" onClick={() => navigate(`/admin/developer-api?tab=log&key=${apiKey.key_id}`)} className="h-7 px-2.5 rounded-md border border-stone-200 text-[12.5px] font-medium inline-flex items-center gap-1 hover:bg-stone-50">
                                Open in request log <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                        {recent.length ? (
                            <div className="flex flex-col">
                                {recent.map((r, i) => (
                                    <div key={`${r.request_id}-${i}`} className="grid grid-cols-[72px_1fr_auto] gap-2.5 items-center py-2 border-b border-stone-100 last:border-b-0 text-[13px]">
                                        <span className="text-stone-400 tabular-nums">{formatLogTime(r.created_at)}</span>
                                        <span><Method method={r.method} /><span className="font-mono text-xs">{r.path_template}</span></span>
                                        <StatusCode status={r.status} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="border border-dashed border-stone-200 rounded-xl bg-stone-50 p-5 text-center text-sm text-stone-500">No requests in the last 30 days.</div>
                        )}
                    </section>
                </div>

                <section className="border border-stone-200 rounded-xl px-5 py-4">
                    <div className="flex items-center gap-2 mb-3.5">
                        <Settings className="w-4 h-4 text-stone-500" />
                        <b className="flex-1 text-[15px] font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</b>
                        {!revoked && !settingsDraft && (
                            <button type="button" data-testid="edit-settings" onClick={editSettings} className="h-7 px-2.5 rounded-md border border-stone-200 text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                        )}
                    </div>
                    {settingsDraft && (
                        <div className="flex flex-col gap-3 mb-4 pb-4 border-b border-stone-100" data-testid="settings-editor">
                            <label className="flex flex-col gap-1 text-[13px] font-medium text-stone-700">
                                Default owner
                                <select
                                    value={settingsDraft.ownerId}
                                    onChange={(e) => setSettingsDraft((d) => ({ ...d, ownerId: e.target.value }))}
                                    className="h-9 px-2 border border-stone-200 rounded-md bg-white text-sm font-normal"
                                    data-testid="settings-owner"
                                >
                                    {!team.some((m) => m.user_id === settingsDraft.ownerId) && (
                                        <option value={settingsDraft.ownerId}>{apiKey.default_owner?.name || 'Pick a teammate'}</option>
                                    )}
                                    {team.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                                </select>
                            </label>
                            <label className="flex flex-col gap-1 text-[13px] font-medium text-stone-700">
                                Expiry
                                <select
                                    value={settingsDraft.expiry}
                                    onChange={(e) => setSettingsDraft((d) => ({ ...d, expiry: e.target.value }))}
                                    className="h-9 px-2 border border-stone-200 rounded-md bg-white text-sm font-normal"
                                    data-testid="settings-expiry"
                                >
                                    <option value="keep">Keep — {apiKey.expires_at ? formatDate(apiKey.expires_at) : 'never'}</option>
                                    {EXPIRY_OPTIONS.map((o) => (
                                        <option key={o.label} value={o.label}>{o.days ? `${o.label} from today` : 'Never expires'}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setSettingsDraft(null)} className="h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium hover:bg-stone-50">Cancel</button>
                                <button type="button" data-testid="save-settings" disabled={saving} onClick={saveSettings} className="h-8 px-3 rounded-md bg-stone-900 text-white text-[13px] font-medium hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400">
                                    Save settings
                                </button>
                            </div>
                        </div>
                    )}
                    <dl className="grid grid-cols-[140px_1fr] gap-x-3.5 gap-y-2.5 text-sm m-0">
                        <dt className="text-stone-500">Default owner</dt>
                        <dd className="m-0">
                            {apiKey.default_owner?.name || '—'}
                            {apiKey.default_owner && !apiKey.default_owner.active && <span className="block text-xs text-amber-700">No longer active — leads that name no one have no owner to go to. Use Edit to pick another.</span>}
                            <span className="block text-xs text-stone-500 mt-0.5">Unless the lead names a teammate</span>
                        </dd>
                        <dt className="text-stone-500">Source tag</dt><dd className="m-0"><span className="inline-flex font-mono text-[11px] font-medium text-stone-600 bg-stone-100 rounded px-1.5 py-0.5">{apiKey.source_tag}</span></dd>
                        <dt className="text-stone-500">Expires</dt><dd className="m-0">{apiKey.expires_at ? formatDate(apiKey.expires_at) : 'Never'}</dd>
                        <dt className="text-stone-500">Allowed IPs</dt>
                        <dd className="m-0">
                            {apiKey.ip_allowlist?.length
                                ? apiKey.ip_allowlist.map((ip) => <span key={ip} className="inline-flex font-mono text-[11px] font-medium text-stone-600 bg-stone-100 rounded px-1.5 py-0.5 mr-1 mb-1">{ip}</span>)
                                : <span className="text-sm">Any IP</span>}
                        </dd>
                        <dt className="text-stone-500">Rate limit</dt><dd className="m-0">{apiKey.rate_limit_per_min} requests per minute</dd>
                        <dt className="text-stone-500">Last used from</dt><dd className="m-0 font-mono text-xs">{apiKey.last_used_ip || '—'}</dd>
                        {apiKey.previous_secret_valid_until && (<><dt className="text-stone-500">Old secret works until</dt><dd className="m-0">{new Date(apiKey.previous_secret_valid_until).toLocaleString()}</dd></>)}
                        {revoked && (<><dt className="text-stone-500">Revoked</dt><dd className="m-0">{formatDate(apiKey.revoked_at)} by {apiKey.revoked_by?.name || '—'}</dd></>)}
                    </dl>
                    <p className="text-[12.5px] text-stone-400 mt-3.5">
                        Use the source tag to start a campaign only for leads that came from this key, for example an auto-reply email.
                    </p>
                </section>
            </div>

            {rolling && (
                <RollKeyDialog
                    apiKey={apiKey}
                    headers={headers}
                    onClose={() => setRolling(false)}
                    onRolled={(rolled, graceLabel) => {
                        setRolling(false);
                        setReveal({ result: rolled, graceLabel });
                        load();
                    }}
                />
            )}
            {revoking && (
                <RevokeKeyDialog
                    apiKey={apiKey}
                    headers={headers}
                    onClose={() => setRevoking(false)}
                    onRollInstead={() => {
                        setRevoking(false);
                        setRolling(true);
                    }}
                    onRevoked={() => {
                        setRevoking(false);
                        load();
                    }}
                />
            )}
            <RevealKeyDialog result={reveal?.result} rolledGraceLabel={reveal?.graceLabel} onClose={() => setReveal(null)} />
        </AdminLayout>
    );
}
